import React from "react";
import BoldbraveLogo from "@/assets/images/logo.png";

export type CVProps = {
  name: string;
  title: string;
  subtitle?: string;
  photo?: string;
  quote?: {
    text: string;
    author: string;
    authorTitle: string;
  };
  summary: string;
  skills: Record<string, string[]>;
  experience: {
    role: string;
    company?: string;
    period: string;
    description: string;
    highlights?: string[];
  }[];
  education?: {
    degree: string;
    institution: string;
    year: string;
  }[];
  certifications?: {
    name: string;
    issuer?: string;
    year?: string;
  }[];
  otherActivities?: {
    title: string;
    description: string;
  };
  variants?: {
    label: string;
    value: string;
  }[];
  currentVariant?: string;
  onVariantChange?: (variant: string) => void;
};

export const CV: React.FC<CVProps> = ({
  name,
  title,
  subtitle,
  photo,
  quote,
  summary,
  skills,
  experience,
  education,
  certifications,
  otherActivities,
  variants,
  currentVariant,
  onVariantChange,
}) => {
  const [showQuote, setShowQuote] = React.useState(!!quote);

  React.useEffect(() => {
    let meta = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "robots";
      document.head.appendChild(meta);
    }
    meta.content = "noindex, nofollow";
    return () => { if (meta) meta.remove(); };
  }, []);

  return (
    <>
      {/* Print-specific styles */}
      <style>{`
        @media print {
          body {
            background: white !important;
            color: black !important;
          }
          
          .cv-container {
            max-width: 100% !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          
          .no-print {
            display: none !important;
          }
          
          .cv-content {
            page-break-inside: avoid;
          }
          
          .cv-section {
            page-break-inside: avoid;
          }
          
          a {
            color: black !important;
            text-decoration: none !important;
          }
          
          /* Keep skill badges styled in print */
          .cv-section span[class*="bg-gray"] {
            background-color: #f3f4f6 !important;
            color: #1f2937 !important;
            border: 1px solid #e5e7eb !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          .cv-logo {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
        
        @media screen {
          .cv-container {
            background: white;
            color: #1a1a1a;
            min-height: 100vh;
            padding: 2rem;
          }
        }
        
        @page {
          margin: 2cm;
          size: A4;
        }
      `}</style>

      <div className="cv-container mx-auto max-w-4xl">
        {/* Screen-only navigation and print button */}
        <div className="no-print mb-6 flex items-center gap-4">
          <a
            href="/cv"
            className="text-gray-600 border border-gray-300 rounded px-3 py-2 text-sm hover:border-gray-400 transition"
          >
            &larr; All CVs
          </a>
          <select
            onChange={(e) => {
              if (e.target.value) {
                window.location.href = e.target.value;
              }
            }}
            className="text-gray-600 border border-gray-300 rounded px-3 py-2 text-sm hover:border-gray-400 focus:outline-none focus:border-[#FF893F]"
            defaultValue=""
          >
            <option value="">Select team member CV...</option>
            <option value="/cv/alexander-nyberg">Alexander Nyberg</option>
            <option value="/cv/anders-soderlin">Anders Söderlin</option>
            <option value="/cv/anton-alfsson">Anton Alfsson</option>
            <option value="/cv/arun-rathi">Arun Rathi</option>
            <option value="/cv/felix-tran">Felix Tran</option>
            <option value="/cv/joen-mauritzon">Joen Mauritzon</option>
            <option value="/cv/jerry-hon">Jerry Hon</option>
            <option value="/cv/johan-rosengren">Johan Rosengren</option>
            <option value="/cv/johannes-tegner">Johannes Tegnér</option>
            <option value="/cv/lyupcho-kotev">Lyupcho Kotev</option>
            <option value="/cv/magnus-christiansson">
              Magnus Christiansson
            </option>
            <option value="/cv/marcel-urrutia-nilsson">
              Marcel Urrutia Nilsson
            </option>
            <option value="/cv/markus-rytterkull">Markus Rytterkull</option>
            <option value="/cv/marie-coletti">Marie Coletti</option>
            <option value="/cv/nell-levasseur">Nell Levasseur</option>
            <option value="/cv/nils-broman">Nils Broman</option>
            <option value="/cv/patrick-fogwall">Patrick Fogwall</option>
            <option value="/cv/patrik-pochill">Patrik Pochill</option>
            <option value="/cv/patrik-rizell">Patrik Rizell</option>
            <option value="/cv/philip-persson">Philip Persson</option>
            <option value="/cv/tomas-boremalm">Tomas Boremalm</option>
            <option value="/cv/vincent-lam">Vincent Lam</option>
            <option value="/cv/viktor-nilsson">Viktor Nilsson</option>
            <option value="/cv/thomas-bergstrom">Thomas Bergström</option>
          </select>
          {quote && (
            <label className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showQuote}
                onChange={(e) => setShowQuote(e.target.checked)}
                className="accent-[#FF893F]"
              />
              Quote
            </label>
          )}
          {variants && variants.length > 0 && onVariantChange && (
            <select
              onChange={(e) => onVariantChange(e.target.value)}
              value={currentVariant || ""}
              className="text-gray-600 border border-gray-300 rounded px-3 py-2 text-sm hover:border-gray-400 focus:outline-none focus:border-[#FF893F]"
            >
              {variants.map((v) => (
                <option key={v.value} value={v.value}>
                  {v.label}
                </option>
              ))}
            </select>
          )}
          <button
            onClick={() => window.print()}
            className="bg-[#FF893F] text-white px-4 py-2 rounded hover:bg-[#FF7A2F] text-sm"
          >
            Print / Save as PDF
          </button>
        </div>

        <div className="cv-content bg-white">
          {/* Header */}
          <header className="cv-section pb-6 mb-6">
            <div className="flex items-start gap-6">
              {photo && (
                <div className="relative w-36 h-36 shrink-0">
                  <img
                    src={photo}
                    alt={name}
                    className="w-full h-full rounded-full object-cover"
                  />
                  <img
                    src={BoldbraveLogo}
                    alt="Boldbrave logo"
                    className="cv-logo cv-logo-badge absolute -bottom-2 -right-2 w-14 h-14 rounded-full bg-white shadow-md object-contain"
                  />
                </div>
              )}
              <div className="flex-1">
                <h1 className="text-4xl font-bold text-gray-900 mb-2">
                  {name}
                </h1>
                <h2 className="text-xl text-gray-600 mb-3">{title}</h2>
                {subtitle && (
                  <p className="text-base text-gray-700">{subtitle}</p>
                )}
                {/* ponytail: no personal contact details — all contact goes through Bold n Brave */}
              </div>
            </div>
          </header>

          {/* Quote */}
          {quote && showQuote && (
            <section className="cv-section mb-8">
              <blockquote className="border-l-4 border-[#FF893F] pl-4 py-2">
                <p className="text-gray-700 italic leading-relaxed">
                  "{quote.text}"
                </p>
                <footer className="mt-2 text-sm text-gray-600">
                  — {quote.author}, {quote.authorTitle}
                </footer>
              </blockquote>
            </section>
          )}

          {/* Summary */}
          <section className="cv-section mb-8">
            <h3 className="text-sm font-bold text-gray-900 mb-2 uppercase tracking-widest border-b border-gray-200 pb-1">
              Summary
            </h3>
            <p className="text-gray-700 leading-relaxed">{summary}</p>
          </section>

          {/* Technical Skills */}
          <section className="cv-section mb-8">
            <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-widest border-b border-gray-200 pb-1">
              Technical Skills
            </h3>
            <div className="space-y-2">
              {Object.entries(skills).map(([category, items]) => (
                <div key={category} className="flex gap-2">
                  <span className="text-sm font-semibold text-gray-900 min-w-[140px] shrink-0">
                    {category}
                  </span>
                  <span className="text-sm text-gray-700">
                    {items.join(" · ")}
                  </span>
                </div>
              ))}
            </div>
          </section>

          {/* Work Experience */}
          <section className="cv-section mb-8">
            <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-widest border-b border-gray-200 pb-1">
              Work Experience
            </h3>
            <div className="space-y-5">
              {experience.map((exp, idx) => (
                <div key={idx}>
                  <div className="flex justify-between items-baseline mb-0.5">
                    <h4 className="text-base font-bold text-gray-900">
                      {exp.role}
                    </h4>
                    <span className="text-sm text-gray-500 shrink-0 ml-4">
                      {exp.period}
                    </span>
                  </div>
                  {exp.company && (
                    <p className="text-sm font-medium text-gray-600 mb-1">
                      {exp.company}
                    </p>
                  )}
                  <p className="text-sm text-gray-700 mb-1.5">
                    {exp.description}
                  </p>
                  {exp.highlights && exp.highlights.length > 0 && (
                    <ul className="text-sm text-gray-700 space-y-0.5 ml-4">
                      {exp.highlights.map((highlight, hidx) => (
                        <li
                          key={hidx}
                          className="relative pl-3 before:content-['–'] before:absolute before:left-0 before:text-gray-400"
                        >
                          {highlight}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Education */}
          {education && education.length > 0 && (
            <section className="cv-section mb-8">
              <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-widest border-b border-gray-200 pb-1">
                Education
              </h3>
              <div className="space-y-3">
                {education.map((edu, idx) => (
                  <div key={idx}>
                    <h4 className="text-sm font-bold text-gray-900">
                      {edu.degree}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {edu.institution} · {edu.year}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Certifications */}
          {certifications && certifications.length > 0 && (
            <section className="cv-section mb-8">
              <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-widest border-b border-gray-200 pb-1">
                Certifications
              </h3>
              <div className="space-y-3">
                {certifications.map((cert, idx) => (
                  <div key={idx}>
                    <h4 className="text-sm font-bold text-gray-900">
                      {cert.name}
                    </h4>
                    {(cert.issuer || cert.year) && (
                      <p className="text-sm text-gray-600">
                        {[cert.issuer, cert.year].filter(Boolean).join(" · ")}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Other Activities */}
          {otherActivities && (
            <section className="cv-section mb-8">
              <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-widest border-b border-gray-200 pb-1">
                {otherActivities.title}
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed">
                {otherActivities.description}
              </p>
            </section>
          )}
        </div>
      </div>
    </>
  );
};
